import clsx from "clsx";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEye,
  faSpinner,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import {
  downloadCaseDocumentFile,
  viewCaseDocumentFile,
} from "@/services/api/case-checking";
import { caseDocumentCanViewInline } from "@/components/case-checking/caseDocumentFile";

export type CaseDocumentFileRef = {
  id: string;
  filename: string;
  file_type: string;
};

const ICON_BTN =
  "rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-white/[0.06] hover:text-slate-700 dark:hover:text-slate-200 disabled:pointer-events-none disabled:opacity-40";

export function CaseDocumentFileActions({
  caseId,
  doc,
  disabled = false,
  className,
  testIdPrefix,
  variant = "icons",
  fixedSlots = false,
}: {
  caseId: string;
  doc: CaseDocumentFileRef;
  disabled?: boolean;
  className?: string;
  testIdPrefix?: string;
  variant?: "icons" | "labeled";
  /** Reserve download/view columns so rows stay aligned when view is unavailable. */
  fixedSlots?: boolean;
}) {
  const [busy, setBusy] = useState<"view" | "download" | null>(null);
  const canView = caseDocumentCanViewInline(doc.file_type, doc.filename);
  const prefix = testIdPrefix ?? doc.id;

  const run = async (kind: "view" | "download") => {
    setBusy(kind);
    try {
      if (kind === "view") {
        await viewCaseDocumentFile(caseId, doc);
      } else {
        await downloadCaseDocumentFile(caseId, doc);
      }
    } catch (err) {
      console.error(`Failed to ${kind} document:`, err);
    } finally {
      setBusy(null);
    }
  };

  if (variant === "labeled") {
    const downloadButton = (
      <Button
        plain
        type="button"
        disabled={disabled || Boolean(busy)}
        onClick={() => void run("download")}
        icon={
          <FontAwesomeIcon
            icon={busy === "download" ? faSpinner : faDownload}
            className={clsx("h-3.5 w-3.5", busy === "download" && "animate-spin")}
          />
        }
        aria-label={`Download ${doc.filename}`}
        data-testid={`${prefix}-download`}
      >
        Download
      </Button>
    );

    const viewButton = (
      <Button
        plain
        type="button"
        disabled={disabled || Boolean(busy)}
        onClick={() => void run("view")}
        icon={
          <FontAwesomeIcon
            icon={busy === "view" ? faSpinner : faEye}
            className={clsx("h-3.5 w-3.5", busy === "view" && "animate-spin")}
          />
        }
        aria-label={`View ${doc.filename}`}
        data-testid={`${prefix}-view`}
      >
        View
      </Button>
    );

    const viewPlaceholder = (
      <Button
        plain
        type="button"
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none invisible"
        icon={<FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />}
      >
        View
      </Button>
    );

    if (fixedSlots) {
      return (
        <div className={clsx("grid grid-cols-[6.5rem_4.5rem] items-center gap-x-3", className)}>
          <div className="flex justify-end">{downloadButton}</div>
          <div className="flex justify-end">{canView ? viewButton : viewPlaceholder}</div>
        </div>
      );
    }

    return (
      <div className={clsx("flex items-center gap-3", className)}>
        {downloadButton}
        {canView ? viewButton : null}
      </div>
    );
  }

  const downloadIconButton = (
    <Button
      plain size="unstyled"
      type="button"
      className={clsx("p-1.5", ICON_BTN)}
      disabled={disabled || Boolean(busy)}
      onClick={() => void run("download")}
      title="Download"
      aria-label={`Download ${doc.filename}`}
      data-testid={`${prefix}-download`}
    >
      <FontAwesomeIcon
        icon={busy === "download" ? faSpinner : faDownload}
        className={clsx("h-3.5 w-3.5", busy === "download" && "animate-spin")}
      />
    </Button>
  );

  const viewIconButton = (
    <Button
      plain size="unstyled"
      type="button"
      className={clsx("p-1.5", ICON_BTN)}
      disabled={disabled || Boolean(busy)}
      onClick={() => void run("view")}
      title="View"
      aria-label={`View ${doc.filename}`}
      data-testid={`${prefix}-view`}
    >
      <FontAwesomeIcon
        icon={busy === "view" ? faSpinner : faEye}
        className={clsx("h-3.5 w-3.5", busy === "view" && "animate-spin")}
      />
    </Button>
  );

  const viewIconPlaceholder = (
    <span className="inline-flex h-8 w-8 shrink-0" aria-hidden />
  );

  if (fixedSlots) {
    return (
      <div className={clsx("grid grid-cols-[2rem_2rem] items-center gap-x-0.5", className)}>
        <div className="flex justify-end">{downloadIconButton}</div>
        <div className="flex justify-end">{canView ? viewIconButton : viewIconPlaceholder}</div>
      </div>
    );
  }

  return (
    <div className={clsx("flex items-center gap-0.5", className)}>
      {downloadIconButton}
      {canView ? viewIconButton : null}
    </div>
  );
}
